import { Suspense, useEffect, useLayoutEffect, useRef } from 'react';

import type { DxfSceneState } from '@frontend/api';
import {
  Box,
  Center,
  Spinner,
  type BoxProps,
  Text,
  VStack,
} from '@frontend/legacy-components';

import { GenericError } from 'modules/common/components/errorBoundary/GenericError';

import { useLiveDxfScene } from 'utils/hooks/taskTracking/useLiveDxfScene';

import { DxfSceneCanvas } from './DxfSceneCanvasLazy';
import { useDxfViewerSession } from './DxfViewerContext';
import { DxfBrushCursorBadge } from './components/DxfBrushCursorBadge';
import { DxfDetailsOverlay } from './components/DxfDetailsOverlay';
import { DxfMappingContextMenu } from './components/DxfMappingContextMenu';
import { DxfMappingMenu } from './components/DxfMappingMenu';
import { ToolLayer } from './components/ToolLayer';
import {
  DXF_CANVAS_BACKGROUND,
  DXF_Z_INDEX,
  MONOCHROME_COLOR,
  TEXT_LAYER_COLOR,
} from './constants';
import { useDxfAxisDimensionsOverlay } from './hooks/useDxfAxisDimensionsOverlay';
import { useDxfDetailsOverlay } from './hooks/useDxfDetailsOverlay';
import { useDxfMarqueeOverlay } from './hooks/useDxfMarqueeOverlay';
import { useDxfMeasureOverlay } from './hooks/useDxfMeasureOverlay';
import { useDxfMeasurementFormat } from './hooks/useDxfMeasurementFormat';
import { useDxfOptimalBoxOverlay } from './hooks/useDxfOptimalBoxOverlay';
import { useDxfSceneLoad } from './hooks/useDxfSceneLoad';
import { useDxfSceneMapping } from './hooks/useDxfSceneMapping';
import { useDxfSceneUnits } from './hooks/useDxfSceneUnits';
import { useDxfTextOverlay } from './hooks/useDxfTextOverlay';
import type { DrawingModel, DxfViewerSession } from './session/types';
import { useDxfLoad } from './session/useSessionState';
import type { MeasurementFormat } from './utils/formatMeasurement';

type DXFViewerProps = {
  /** Identifies the scene to fetch and watch; `null` when this viewer is not the active kind. */
  partFileUuid: string | null;
  /**
   * `PartFile.dxfSceneState` as of the last part-payload fetch; `null` when this
   * viewer is not the active kind. `building`/`regenerating` mean no blob is on
   * offer yet, which this viewer reports while it waits for one.
   */
  scene: DxfSceneState | null;
  /** Hidden rather than unmounted when another viewer is active. */
  isPaused: boolean;
  /** Render the text layer over the geometry. */
  isTextVisible?: boolean;
  /** When editing is blocked, copy for disabled mapping controls. */
  canEditPartInQuoteDisabledReason?: string;
} & Omit<BoxProps, 'children'>;

type DxfReadyViewProps = {
  session: DxfViewerSession;
  model: DrawingModel;
  isPaused: boolean;
  format: MeasurementFormat;
};

/**
 * Canvas, rotate, details — anything that needs a derived drawing. Overlay
 * *hosts* stay in the shell so their refs bind on first paint. Units are
 * published from the shell (the scene state's `units`), not from the scene blob.
 */
const DxfReadyView = ({
  session,
  model,
  isPaused,
  format,
}: DxfReadyViewProps) => {
  const details = useDxfDetailsOverlay(session, model, format);

  return (
    <>
      <Suspense fallback={null}>
        <DxfSceneCanvas
          onAttach={session.attachRenderer}
          backgroundColor={DXF_CANVAS_BACKGROUND}
          monochromeColor={MONOCHROME_COLOR}
          zIndex={DXF_Z_INDEX.CANVAS}
        />
      </Suspense>
      {!isPaused && details && (
        <DxfDetailsOverlay
          {...details}
          position="absolute"
          bottom={2}
          right={2}
          zIndex={DXF_Z_INDEX.CHROME}
        />
      )}
    </>
  );
};
DxfReadyView.displayName = 'DxfReadyView';

/**
 * Base component for the DXF scene viewer — the DXF equivalent of
 * `PDFViewer`/`CADViewer`.
 *
 * The shell always mounts the input surface and overlay hosts. `load.status`
 * is the only gate: spinner / error live here, and a ready drawing mounts
 * `DxfReadyView`. Panning and zooming do not re-render this — the renderer
 * and text layer subscribe to the session's viewport store directly.
 *
 * Unlike PDFTron and the CAD viewer, this viewer is cheap and safe to unmount, so
 * it does not need the mount-latch treatment in `Viewer.tsx` — `isPaused` exists
 * for parity and to keep viewport and layer state across a brief switch away.
 *
 * Everything here is three-free. Geometry is drawn by `DxfSceneCanvas`, which is
 * lazily loaded (see `DxfSceneCanvasLazy`). `scene.texts` is a real DOM layer
 * rather than glyph geometry, by backend design.
 */
export const DXFViewer = ({
  partFileUuid,
  scene: partFileScene,
  isPaused,
  isTextVisible = true,
  canEditPartInQuoteDisabledReason,
  ...boxProps
}: DXFViewerProps) => {
  const session = useDxfViewerSession();
  const surfaceRef = useRef<HTMLDivElement>(null);

  // A pending scene has no URL on the PartFile payload, so ask the scene endpoint
  // for one — which is also what enqueues the build — and wait for the build to
  // announce itself. The response is the same state shape, carrying the URL.
  const { data: liveScene, isError: isSceneStatusError } = useLiveDxfScene({
    partFileUuid,
    enabled:
      (partFileScene?.status === 'building' ||
        partFileScene?.status === 'regenerating') &&
      !isPaused,
  });

  const scene =
    partFileScene?.status === 'ready'
      ? partFileScene
      : liveScene ?? partFileScene;

  const sceneUrl = scene?.status === 'ready' ? scene.authenticatedUrl : null;
  const sceneUnits = scene && 'units' in scene ? scene.units : null;
  const isSceneUnobtainable = isSceneStatusError || scene?.status === 'failed';
  const isAwaitingScene =
    !isSceneStatusError &&
    (scene?.status === 'building' || scene?.status === 'regenerating');

  const loadInput = useDxfSceneLoad(sceneUrl);
  const savedMapping = useDxfSceneMapping(partFileUuid);
  const load = useDxfLoad(session);
  const readyModel = load.status === 'ready' ? load.model : null;

  // Attach before loadScene in the same layout pass so the first fit sees a
  // real container size. loadScene alone can otherwise frame against 0×0
  // (scale=1) when the scene is already cached.
  useLayoutEffect(() => {
    const host = surfaceRef.current;
    return host ? session.attachSurface(host) : undefined;
  }, [session]);

  useLayoutEffect(() => {
    session.setPartFileUuid(partFileUuid);
  }, [session, partFileUuid]);

  useLayoutEffect(() => {
    session.loadScene(loadInput);
    // After loadScene: a new drawing clears saved to null, then this restores
    // the fetched mapping (or confirms there is none). Skip while the query is
    // still unresolved so a pending fetch is not treated as "no mapping row".
    if (savedMapping !== undefined) {
      session.mapping.loadSaved(savedMapping);
    }
  }, [session, loadInput, savedMapping]);

  useEffect(() => {
    session.setPaused(isPaused);
  }, [session, isPaused]);

  /**
   * Publishes "this file can no longer be mapped" into the session.
   *
   * The spec's lock — sent in a quote, or used in more than one place — is already
   * computed by `useCanEditPartInQuote`, which covers a finalized quote, a trashed
   * quote, a part in multiple quotes, and another user holding the item. Rather than
   * restate any of that, this pushes the answer across the Redux boundary the same
   * way `setPaused` does, so the session stays free of quote concepts and everything
   * that reads the lock reads one value.
   *
   * Locking mid-session deliberately does not eject the user: the mapping controller
   * refuses further edits and Save is disabled with the reason, which loses nothing
   * they have already done and explains why. Yanking them out of the mode would do
   * neither.
   */
  useEffect(() => {
    session.mapping.setLockReason(canEditPartInQuoteDisabledReason);
  }, [session.mapping, canEditPartInQuoteDisabledReason]);

  useDxfSceneUnits(sceneUnits);
  const format = useDxfMeasurementFormat(sceneUnits);

  const textLayerRef = useDxfTextOverlay({
    model: session.model,
    hiddenLayers: session.hiddenLayers,
    overlays: session.overlays,
    viewport: session.viewport,
  });

  /* Marquee tool */
  const marqueeRef = useDxfMarqueeOverlay({ marquee: session.marquee });

  /* Measure tool */
  const measureRef = useDxfMeasureOverlay({
    measure: session.measure,
    tool: session.tools,
    pointer: session.pointer,
    viewport: session.viewport,
    format,
  });

  /* Axis dimensions tool */
  const axisRef = useDxfAxisDimensionsOverlay({
    model: session.model,
    hiddenLayers: session.hiddenLayers,
    overlays: session.overlays,
    viewport: session.viewport,
    format,
  });

  /* Optimal box tool */
  const optimalBoxRef = useDxfOptimalBoxOverlay({
    model: session.model,
    hiddenLayers: session.hiddenLayers,
    overlays: session.overlays,
    viewport: session.viewport,
    format,
  });

  // The container must stay mounted in every state: it is the input surface and the
  // canvas's host, and unmounting it mid-fetch would discard the WebGL context.
  return (
    <Box
      bg={DXF_CANVAS_BACKGROUND}
      data-testid="dxf-viewer"
      {...boxProps}
      ref={surfaceRef}
    >
      {readyModel && (
        <DxfReadyView
          session={session}
          model={readyModel}
          isPaused={isPaused}
          format={format}
        />
      )}
      {/* Overlay hosts stay mounted so hook effects can bind their refs on first
          paint — gating them on readiness left layerRef null forever. */}
      <Box
        ref={textLayerRef}
        position="absolute"
        inset={0}
        overflow="hidden"
        pointerEvents="none"
        zIndex={DXF_Z_INDEX.SCENE_OVERLAY}
        visibility={readyModel && isTextVisible ? 'visible' : 'hidden'}
        className="dxf-text-layer"
      >
        {readyModel?.texts.map(({ id, text }) => (
          <Box
            as="span"
            key={id}
            position="absolute"
            whiteSpace="pre"
            transformOrigin="left bottom"
            lineHeight={1}
            color={TEXT_LAYER_COLOR}
            userSelect="none"
          >
            {text.s}
          </Box>
        ))}
      </Box>
      {/* Inside the surface so it is positioned in the same coordinate space the
          anchor hook measures in, and above the tool layers so it stays clickable. */}
      {!isPaused && <DxfMappingMenu />}
      {!isPaused && <DxfMappingContextMenu />}
      {!isPaused && <DxfBrushCursorBadge />}
      <ToolLayer ref={marqueeRef} data-testid="dxf-marquee-layer" />
      <ToolLayer ref={measureRef} data-testid="dxf-measure-layer" />
      <ToolLayer ref={axisRef} data-testid="dxf-axis-dimensions-layer" />
      <ToolLayer ref={optimalBoxRef} data-testid="dxf-optimal-box-layer" />
      {(load.status === 'error' || isSceneUnobtainable) && (
        <Center position="absolute" inset={0} zIndex={DXF_Z_INDEX.STATUS}>
          <Box bg="page.surface" borderRadius="lg" p={2} width="max-content">
            <GenericError size="lg" justifyContent="center" />
          </Box>
        </Center>
      )}
      {load.status === 'loading' && (
        <Center position="absolute" inset={0} zIndex={DXF_Z_INDEX.STATUS}>
          <Spinner size="xl" />
        </Center>
      )}
      {isAwaitingScene && (
        <Center position="absolute" inset={0} zIndex={DXF_Z_INDEX.STATUS}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text color={TEXT_LAYER_COLOR}>
              {scene?.status === 'regenerating'
                ? 'Updating this drawing for the latest viewer\u2026'
                : 'Preparing this drawing\u2026'}
            </Text>
          </VStack>
        </Center>
      )}
    </Box>
  );
};
